import type { ReactNode } from 'react';
import { BarChart3, Gauge, Plus, StickyNote, Table2, TrendingUp, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { WIDGET_KIND_LABELS, type WidgetKind } from '@/features/intelligence/dashboard/types';

const WIDGET_KIND_ICONS: Record<WidgetKind, LucideIcon> = {
  metric: Gauge,
  chart: BarChart3,
  forecast: TrendingUp,
  table: Table2,
  text: StickyNote,
};

const KIND_BLURBS: Record<WidgetKind, string> = {
  metric: 'A headline number with its change',
  chart: 'Lines, bars, donut or ranked bars',
  forecast: 'Project a series forward',
  table: 'A ranked list of rows',
  text: 'Context for the section',
};

const KIND_ORDER: WidgetKind[] = ['metric', 'chart', 'table', 'forecast', 'text'];

interface AddWidgetMenuProps {
  onPick: (kind: WidgetKind) => void;
  /** Kinds to list first (the page's bread and butter). */
  featured?: WidgetKind[];
  label?: ReactNode;
  size?: 'sm' | 'default';
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  align?: 'start' | 'end';
  disabled?: boolean;
}

export function AddWidgetMenu({ onPick, featured, label = 'Add widget', size = 'sm', variant = 'outline', align = 'end', disabled }: AddWidgetMenuProps) {
  const order = featured ? [...featured, ...KIND_ORDER.filter((k) => !featured.includes(k))] : KIND_ORDER;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={disabled}>
          <Plus className="h-4 w-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-64">
        <DropdownMenuLabel>Add to this dashboard</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {order.map((kind) => {
          const Icon = WIDGET_KIND_ICONS[kind];
          return (
            <DropdownMenuItem key={kind} onSelect={() => onPick(kind)} className="items-start gap-3 py-2">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.8} />
              <span className="min-w-0">
                <span className="block text-sm">{WIDGET_KIND_LABELS[kind]}</span>
                <span className="block text-xs text-muted-foreground">{KIND_BLURBS[kind]}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
